import { useImperativeHandle, useMemo, useRef } from 'react';
export function useMeasureableHandle(publicRef) {
    const targetRef = useRef(null);
    // Create the imperative handle object with stable identity
    const imperativeHandle = useMemo(() => ({
        /**
         * Returns the underlying host DOM node, bypassing the imperative handle abstraction.
         *
         * **Why this exists:** When `useImperativeHandle` replaces the ref value with a proxy
         * object, Radix UI's internal refs (composed via `Slot` / `useComposedRefs`) also
         * receive that proxy instead of the real DOM element. Radix then passes the proxy to
         * `Node.contains()`, which throws `TypeError: parameter 1 is not of type 'Node'`.
         *
         * This escape hatch lets patched Radix code retrieve the real DOM node:
         * ```
         * target.contains(trigger?.dangerouslyGetHostNode?.() ?? trigger)
         * ```
         *
         * @deprecated Prefer the public `MeasureableHandle` API. This exists solely to work
         * around Radix UI's internal use of `Node.contains()` with composed refs.
         */
        dangerouslyGetHostNode: () => {
            return targetRef.current;
        },
        /**
         * floating-ui fallback for when the trigger fails an `instanceof Element` check (i.e. when
         * it is a proxy object from `useImperativeHandle`). floating-ui reads `contextElement` to
         * get the real DOM node, then traverses its ancestors to attach scroll and resize listeners
         * that keep tooltips and popovers repositioned correctly.
         *
         * @deprecated This getter exists solely because `useImperativeHandle` replaces the ref
         * with a proxy object. A component that exposes a direct `HTMLElement` ref does not need
         * this — floating-ui can traverse ancestors from the real node directly.
         */
        get contextElement() {
            return targetRef.current instanceof Element ? targetRef.current : undefined;
        },
        /**
         * Returns a bounding client rect of the underlying target element. A bounding client
         * rect provides information about the size of an element and its position relative to the viewport.
         */
        getBoundingClientRect: () => {
            // Required by Radix UI's popover primitives
            return targetRef.current?.getBoundingClientRect() ?? DOMRect.fromRect({});
        },
        /**
         * Focus the underlying target element.
         */
        focus: () => {
            targetRef.current?.focus();
        },
        /**
         * Blur (unfocus) the underlying target element.
         */
        blur: () => {
            targetRef.current?.blur();
        },
        /**
         * Required by Radix UI's tooltips [1]
         *
         * [1]: https://github.com/radix-ui/primitives/blob/36d954d3c1b41c96b1d2e875b93fc9362c8c09e6/packages/react/tooltip/src/tooltip.tsx#L449
         *
         * @deprecated - Usage of this method is discouraged as it pierces the React abstraction. Rely on React event callback props instead.
         */
        addEventListener: (type, listener, options) => {
            targetRef.current?.addEventListener(type, listener, options);
        },
        /**
         * Required by Radix UI's tooltips [1]
         *
         * [1]: https://github.com/radix-ui/primitives/blob/36d954d3c1b41c96b1d2e875b93fc9362c8c09e6/packages/react/tooltip/src/tooltip.tsx#L452
         *
         * @deprecated - Usage of this method is discouraged as it pierces the React abstraction. Rely on React event callback props instead.
         */
        removeEventListener: (type, listener, options) => {
            targetRef.current?.removeEventListener(type, listener, options);
        },
        /**
         * Required by Radix UI's tooltips [1]
         *
         * [1]: https://github.com/radix-ui/primitives/blob/36d954d3c1b41c96b1d2e875b93fc9362c8c09e6/packages/react/tooltip/src/tooltip.tsx#L463
         *
         * @deprecated - Usage of this method is discouraged as it pierces the React abstraction. Rely on React refs.
         */
        contains: (node) => {
            if (node != null && !(node instanceof Node))
                return false;
            return targetRef.current?.contains(node) ?? false;
        },
    }), []);
    useImperativeHandle(publicRef, () => imperativeHandle, [imperativeHandle]);
    return targetRef;
}
//# sourceMappingURL=useMeasureableHandle.js.map